import { Link } from "react-router-dom";
import SectionEyebrow from "@/components/shared/SectionEyebrow";
import useSeo from "@/lib/useSeo";

const SECTIONS = [
  {
    h: "1. Acceptance of Terms",
    p: "By accessing or using the IslandHop platform, mobile applications, or websites operated by IslandHop Technologies LLC (\"IslandHop\", \"we\", \"us\"), you agree to be bound by these Terms & Conditions. If you do not agree, please do not use our services.",
  },
  {
    h: "2. Description of Services",
    p: "IslandHop is a technology platform that connects customers with independent delivery contractors and merchant partners across Trinidad & Tobago and the wider Caribbean region. IslandHop provides technology, dispatch, and support infrastructure — it does not directly provide delivery, transportation, or merchant services, and does not own or operate merchant inventory.",
  },
  {
    h: "3. User Accounts",
    p: "You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. You agree to provide accurate, current information and to promptly update it as needed.",
  },
  {
    h: "4. SMS / Text Message Consent",
    p: "If you opt in to SMS notifications by checking the consent checkbox on our Driver, Partner, or other application forms, you consent to receive automated transactional text messages from IslandHop Technologies LLC at the phone number you provided. Messages may include application status updates, onboarding instructions, delivery notifications, driver dispatch messages, and account alerts. Message frequency varies. Standard message and data rates from your mobile carrier may apply. You may opt out at any time by replying STOP to any message; reply HELP for assistance. Consent to receive SMS is not a condition of purchase or use of IslandHop's services. IslandHop does not share mobile phone numbers or SMS consent data with third parties for their marketing purposes.",
  },
  {
    h: "5. Payments & Fees",
    p: "Customers pay for goods and applicable delivery fees through IslandHop's payment partners. Merchants are charged commission (10–25%) and optional subscription fees per their signed merchant agreement. Prices, taxes, and delivery fees are disclosed prior to order confirmation.",
  },
  {
    h: "6. Independent Contractors",
    p: "Drivers who deliver through the IslandHop platform operate as independent contractors, not as employees, agents, or representatives of IslandHop. IslandHop does not control the manner, time, or location in which drivers perform deliveries.",
  },
  {
    h: "7. Prohibited Use",
    p: "You may not use IslandHop for illegal goods or services, harassment, fraud, spam, or any activity that violates applicable law or the rights of any third party.",
  },
  {
    h: "8. Limitation of Liability",
    p: "To the maximum extent permitted by law, IslandHop Technologies LLC and its affiliates, officers, employees, and partners are not liable for indirect, incidental, special, consequential, or punitive damages arising from your use of the platform. Our aggregate liability for any claim will not exceed the amount you paid to IslandHop in the 90 days preceding the claim.",
  },
  {
    h: "9. Governing Law",
    p: "These Terms are governed by the laws of the State of Delaware, United States, without regard to conflict-of-law principles, and the applicable laws of the Republic of Trinidad & Tobago for services rendered within that jurisdiction. Disputes will be resolved in the competent courts of these jurisdictions.",
  },
  {
    h: "10. Changes to these Terms",
    p: "We may update these Terms from time to time. Continued use of the platform after changes are posted constitutes acceptance of the revised Terms.",
  },
  {
    h: "11. Contact Us",
    p: "Questions about these Terms & Conditions? Contact support@islandhoptt.com. IslandHop Technologies LLC — Port of Spain, Trinidad & Tobago.",
  },
];

export default function TermsAndConditions() {
  useSeo({
    title: "Terms & Conditions",
    description:
      "IslandHop Technologies LLC Terms & Conditions — service description, user obligations, SMS consent terms, liability, and governing law.",
  });

  return (
    <div data-testid="terms-and-conditions-page">
      <section className="relative pt-20 lg:pt-28 pb-12 border-b border-ih-line overflow-hidden">
        <div className="absolute inset-0 ih-grid-bg opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 space-y-5">
          <SectionEyebrow label="Terms" color="electric" />
          <h1 className="font-display font-black text-5xl lg:text-6xl text-white">
            Terms &amp; Conditions
          </h1>
          <p className="text-white/40 font-mono text-xs uppercase tracking-[0.18em]">
            Last updated · February 2026
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 space-y-8">
          {SECTIONS.map((s, i) => (
            <div key={i} className="space-y-3">
              <h2 className="font-display font-bold text-2xl text-white">{s.h}</h2>
              <p className="text-white/70 leading-relaxed">{s.p}</p>
            </div>
          ))}

          <div className="mt-12 pt-8 border-t border-ih-line flex flex-wrap gap-3">
            <Link to="/privacy-policy" className={chip(false)} data-testid="legal-tab-privacy">
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" className={chip(true)} data-testid="legal-tab-terms">
              Terms &amp; Conditions
            </Link>
            <Link to="/refund-policy" className={chip(false)} data-testid="legal-tab-refund">
              Refund Policy
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

const chip = (active) =>
  `px-4 py-2 rounded-full text-sm border transition ${
    active
      ? "bg-white text-black border-white"
      : "border-ih-line2 text-white/70 hover:text-white hover:border-white/30"
  }`;
